import { useMemo, useState } from "react";
import type { Account, DirectoryUser } from "../domain/types";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";

export function NewConversationDialog({
  account,
  users,
  busy,
  onClose,
  onCreate,
}: {
  readonly account: Account;
  readonly users: readonly DirectoryUser[];
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onCreate: (input: {
    type: "p2p" | "group" | "broadcast";
    title?: string;
    users: readonly DirectoryUser[];
  }) => void;
}) {
  const [type, setType] = useState<"p2p" | "group" | "broadcast">("p2p");
  const [title, setTitle] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const candidates = useMemo(() => users.filter((user) => {
    if (user.userId === account.userId) return false;
    const value = query.trim().toLocaleLowerCase();
    return value.length === 0 || user.displayName.toLocaleLowerCase().includes(value) ||
      user.email?.toLocaleLowerCase().includes(value) === true;
  }), [account.userId, query, users]);
  const canCreate = selected.size > 0 && (type !== "p2p" || selected.size === 1) &&
    (type === "p2p" || title.trim().length > 0);

  function toggle(userId: string): void {
    setSelected((current) => {
      const next = new Set(type === "p2p" ? [] : current);
      if (current.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="modal-card new-chat-modal" role="dialog" aria-modal="true"
        aria-label="新建会话" onMouseDown={(event) => { event.stopPropagation(); }}>
        <header className="modal-header">
          <div><p className="eyebrow">NEW CONVERSATION</p><h2>发起新会话</h2></div>
          <button className="icon-button" type="button" aria-label="关闭新建会话" onClick={onClose}><Icon name="close" /></button>
        </header>
        <div className="type-tabs" role="tablist" aria-label="会话类型">
          {(["p2p", "group", "broadcast"] as const).map((value) => (
            <button key={value} type="button" role="tab" aria-selected={type === value}
              className={type === value ? "active" : ""}
              onClick={() => { setType(value); setSelected(new Set()); }}>
              {value === "p2p" ? "私聊" : value === "group" ? "群聊" : "广播"}
            </button>
          ))}
        </div>
        {type === "p2p" ? null : (
          <label className="form-field"><span>{type === "broadcast" ? "广播名称" : "群名称"}</span>
            <input value={title} maxLength={128} onChange={(event) => { setTitle(event.target.value); }}
              placeholder={type === "broadcast" ? "例如：产品公告" : "例如：项目讨论组"} />
          </label>
        )}
        <label className="search-field modal-search"><Icon name="search" />
          <input value={query} onChange={(event) => { setQuery(event.target.value); }} placeholder="搜索联系人" />
        </label>
        <div className="contact-picker">
          {candidates.length === 0 ? <p className="picker-empty">暂无可选联系人。对方首次登录后会出现在这里。</p> :
            candidates.map((user) => (
              <button type="button" key={user.userId} className={selected.has(user.userId) ? "selected" : ""}
                onClick={() => { toggle(user.userId); }}>
                <Avatar name={user.displayName} url={user.avatarUrl} size={40} />
                <span><strong>{user.displayName}</strong><small>{user.email ?? user.userId}</small></span>
                <i>{selected.has(user.userId) ? "✓" : ""}</i>
              </button>
            ))}
        </div>
        <footer className="modal-actions">
          <span>已选择 {selected.size} 人</span>
          <button type="button" className="primary" disabled={!canCreate || busy} onClick={() => { onCreate({
            type,
            ...(type === "p2p" ? {} : { title: title.trim() }),
            users: users.filter((user) => selected.has(user.userId)),
          }); }}>{busy ? "创建中…" : "开始聊天"}</button>
        </footer>
      </section>
    </div>
  );
}
